import { execOnLoad, formatPrice } from '@general/helpers';
import Swiper from 'swiper';
import { EffectFade, Navigation, Pagination } from 'swiper/modules';
import 'swiper/scss';
import 'swiper/scss/navigation';
import { initProductForm } from '@components/product';
import { openMinicart } from '@components/minicart';
import breakpoints from 'breakpoints';
import { throttle } from 'lodash';
import { addToWishlist, getWishlist, deleteFromWishlist, setWishlistStatus } from '@components/wishlist';

const BASE_CLASS = 'product-tile__container';
const INITIALIZED_CLASS = `${BASE_CLASS}--initialized`;

function resetFormOptions(form) {
  if (!form) return;
  form.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
    input.checked = false;
  });
  const idInput = form.querySelector('input[name="id"]');
  if (idInput && !form.classList.contains('product-tile__form--single-variant')) {
    idInput.value = '';
  }
}

function handleColorSwatch(snippetContainer) {
  snippetContainer
    .querySelectorAll('.product-tile__meta .product-tile__color-options .product-tile__colors-variant > a')
    .forEach((colorSwatch) => {
      colorSwatch.addEventListener('click', (e) => {
        e.preventDefault();
        if (colorSwatch.classList.contains('product-tile__colors-variant--current')) return;
        const url = new URL(colorSwatch.href);
        const params = url.search.replace('?', '').split('&').filter(Boolean);
        params.push('view=tile');
        url.search = `?${params.join('&')}`;
        fetch(url.href)
          .then((res) => res.text())
          .then((data) => {
            const newTileDom = new DOMParser().parseFromString(data, 'text/html');
            const newContainer = newTileDom.querySelector(`.${BASE_CLASS}`);
            snippetContainer.classList.remove(INITIALIZED_CLASS);
            snippetContainer.innerHTML = newContainer.innerHTML;
            // Only innerHTML is swapped, so the wrapper's accessible name must be
            // refreshed by hand or it keeps naming the previous product.
            const newLabel = newContainer.getAttribute('aria-label');
            if (newLabel) snippetContainer.setAttribute('aria-label', newLabel);
            window.dAgency.initProductTile(snippetContainer);
          });
      });
    });
}

function initGallerySwiper(snippetContainer) {
  const swiperWrapper = snippetContainer.querySelector('.product-tile__gallery > .swiper-wrapper');
  if (!swiperWrapper || swiperWrapper.querySelectorAll('.swiper-slide').length <= 1) return;
  const swiperContainer = swiperWrapper.closest('.product-tile__gallery');
  const pagination = swiperContainer.querySelector('.product-tile__gallery-pagination');
  const options = {
    modules: [EffectFade, Navigation, ...(pagination ? [Pagination] : [])],
    crossFade: true,
    effect: 'fade',
    navigation: {
      prevEl: swiperContainer.querySelector('.product-tile__gallery-arrow--left'),
      nextEl: swiperContainer.querySelector('.product-tile__gallery-arrow--right'),
    },
    ...(pagination ? { pagination: { el: pagination } } : {}),
  };
  let autoChange = true,
    changeEnter = false,
    changeLeave = false;
  const swiper = new Swiper(swiperContainer, options);
  swiperContainer.addEventListener('mouseenter', () => {
    if (window.innerWidth <= parseInt(breakpoints.lg) || !autoChange) return;
    changeEnter = true;
    swiper.slideTo(1);
  });
  swiperContainer.addEventListener('mouseleave', () => {
    if (window.innerWidth <= parseInt(breakpoints.lg) || !autoChange) return;
    changeLeave = true;
    swiper.slideTo(0);
  });
  swiper.on('slideChangeTransitionStart', () => {
    if (!changeEnter && !changeLeave) {
      autoChange = false;
      return;
    }
    if (changeEnter) changeEnter = false;
    if (changeLeave) changeLeave = false;
  });
}

function handleQuickBuy(snippetContainer) {
  const quickBuy = snippetContainer.querySelector('.product-tile__quick-buy');
  const quickBuyWrapper = snippetContainer.querySelector('.product-tile__quick-buy-wrapper');
  const quickBuyClose = snippetContainer.querySelector('.quick-buy__close');
  const form = snippetContainer.querySelector('.product-tile__form');
  const submit = snippetContainer.querySelector('.quick-buy__add-to-cart');
  const error = snippetContainer.querySelector('.quick-buy__error');
  const wishlistButton = snippetContainer.querySelector('[data-wishlist-btn]');
  if (!quickBuy || !form) return;

  resetFormOptions(form);

  quickBuyClose?.addEventListener('click', () => {
    setTimeout(() => {
      quickBuy.classList.remove('hidden');
      resetFormOptions(form);
    }, 400);
    error.classList.add('hidden');
    quickBuyWrapper.classList.remove('active');
    submit.disabled = false;
  });

  submit?.addEventListener('click', (e) => {
    const formData = new FormData(form);
    if (!formData.get('id')) {
      e.preventDefault();
      quickBuyWrapper.classList.add('active');
      setTimeout(() => {
        quickBuy.classList.add('hidden');
      }, 400);
    }
  });

  initProductForm({
    form,
    callbacks: {
      afterVariantChange: async ({ variant, product }) => {
        if (wishlistButton) {
          wishlistButton.dataset.wishlistVariantId = variant?.id;
          wishlistButton.dataset.wishlistPrice = formatPrice({ value: variant?.price ?? 0 });
          wishlistButton.dataset.wishlistCompareAtPrice = variant?.compare_at_price
            ? formatPrice({ value: variant?.compare_at_price })
            : '';
          const wishlist = await getWishlist();
          const wishlistProduct = wishlist?.listcontents?.find(
            (addedProduct) => addedProduct.epi == variant.id && addedProduct.empi == product.id,
          );
          setWishlistStatus(wishlistButton, wishlistProduct ? 'active' : 'inactive');
        }
        if (submit && !submit.disabled) {
          error.classList.add('hidden');
          submit.click();
        } else {
          error.classList.remove('hidden');
        }
      },
      afterSubmit: () => {
        quickBuyClose?.click();
        openMinicart(true);
      },
    },
  });
}

function handleWishlist(snippetContainer) {
  const wishlistButton = snippetContainer.querySelector('[data-wishlist-btn]');
  if (!wishlistButton) return;
  const toggleLoading = throttle(() => {
    wishlistButton.classList.toggle('loading');
  }, 500);
  wishlistButton.addEventListener('click', async (e) => {
    e.preventDefault();
    toggleLoading();
    const variantID = wishlistButton?.dataset?.wishlistVariantId;
    const masterID = wishlistButton?.dataset?.wishlistProductId;
    const productURL = wishlistButton?.dataset?.wishlistProductUrl;
    const price = wishlistButton?.dataset?.wishlistPrice;
    const compareAtPrice = wishlistButton?.dataset?.wishlistCompareAtPrice;
    if (wishlistButton?.dataset?.wishlistStatus === 'active') {
      await deleteFromWishlist({ variantID, masterID, productURL });
      setWishlistStatus(wishlistButton, 'inactive');
    } else {
      await addToWishlist({ variantID, masterID, productURL, compareAtPrice, price });
      setWishlistStatus(wishlistButton, 'active');
    }
    toggleLoading();
  });
}

if (!window.dAgency) window.dAgency = {};
window.dAgency.initProductTile = (context) => {
  const snippetContainer = context.classList.contains(BASE_CLASS) ? context : context.closest(`.${BASE_CLASS}`);
  if (!snippetContainer || snippetContainer.classList.contains(INITIALIZED_CLASS)) return;

  handleColorSwatch(snippetContainer);
  initGallerySwiper(snippetContainer);
  handleQuickBuy(snippetContainer);
  handleWishlist(snippetContainer);

  snippetContainer.classList.add(INITIALIZED_CLASS);
};

execOnLoad(window.dAgency.initProductTile, `.${BASE_CLASS}`);
